import React from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { CompanyDetails, Detail } from 'store/admins/types';
import { mapPermissionsToOptions } from 'components/RegisterFirm/utils/utils';

import './_FormStyle.scss';
import FormInput from 'shared/Inputs/FormInput';
import { QuoteSelect } from 'shared/Inputs/Quote/QuoteSelect';
import Button from 'shared/Button/Button';
import { telephoneMask } from 'shared/Inputs/inputMasks';
import ErrorManager from 'shared/Messages/ErrorManager';
import { Radio } from '../RegisterFirm/interface/interface';
import { telephoneValidation } from 'utils/form/validation';
import { RegisterFirmHelpTexts } from 'components/RegisterFirm/interface/interface';

type Props = {
  error: string;
  onSubmit: (values: CompanyDetails) => void;
  onBack: () => void;
  companyType: string;
  details: Detail[];
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, 'First Name should contains minimum 2 characters')
    .matches(/[a-zA-Z]/, { message: 'First Name is invalid.' })
    .required('First Name is required.'),
  surname: Yup.string()
    .min(2, 'Surname should contains minimum 2 characters')
    .matches(/[a-zA-Z]/, { message: 'Surname is invalid' })
    .required('Surname is required.'),
  email: Yup.string()
    .typeError('Email Address is invalid')
    .email('Email Address is invalid')
    .required('Email Address is required.'),
  contactNumber: telephoneValidation().required('Contact Number is required.'),
});

const initialFormData = {
  firstName: '',
  surname: '',
  email: '',
  contactNumber: '',
  additionalPermissions: '',
};

const availableAdditionalPermissions: Radio[] = [
  { label: 'Yes - Adviser', value: 'ADVISER' },
  { label: 'Yes - Adviser Support', value: 'ADVISER_SUPPORT' },
  { label: 'No', value: 'NO' },
];

const permissionOptions = mapPermissionsToOptions(availableAdditionalPermissions);

const CompanyDetailsForm: React.FC<Props> = ({ error, onSubmit, companyType, details, onBack }: Props) => {
  return (
    <Formik initialValues={initialFormData} onSubmit={onSubmit} validationSchema={validationSchema}>
      {() => {
        return (
          <Form className="form company-details">
            <div className="form__description form__item">
              <h2 className="page-title">Company details</h2>
              {details.map(({ label, value }: Detail, index) => (
                <p key={`detail-${index}`}>
                  <span>{label}</span>
                  <span>{value}</span>
                </p>
              ))}
            </div>

            <Field name="firstName" component={FormInput} label="First Name" />

            <Field name="surname" component={FormInput} label="Surname" />

            <Field name="email" component={FormInput} label="Email Address" type="email" />

            <Field
              name="contactNumber"
              component={FormInput}
              label="Contact Number"
              mask={telephoneMask}
              showMask={true}
            />

            {companyType === 'FIRM' && (
              <Field
                name="additionalPermissions"
                labelText="Do you require any additional permissions for this account?"
                component={QuoteSelect}
                helperText={RegisterFirmHelpTexts.additionalPermissions}
                options={permissionOptions}
                required
              />
            )}

            <div className="form__navigation">
              <div className="form__navigation-controls">
                <Button className="form__submit" type="button" label="Back" onClick={onBack} />
                <div>
                  <span className="form-failure">
                    <ErrorManager error={error} />
                  </span>
                  <Button className="form__submit" type="submit" label="Next" />
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CompanyDetailsForm;
